import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { fetchLookupPage } from '../../hooks/useLookupSearch';
import { getCollectionCollaborators, searchUsersByEmail } from '../../services/users';
import { commitRows, checkRows } from '../../utils/spreadsheet/importRun';
import { namesInColumn, resolvePeople, resolveRecordNames } from '../../utils/spreadsheet/nameLookups';
import { buildRows, isNameType, mappedColumns, mintRun } from '../../utils/spreadsheet/rows';
import { normalizeId } from '../../utils/ids';

const IDLE = Object.freeze({ phase: 'idle', done: 0, total: 0, result: null, error: null });

/** Where a lookup's records live: the target's owner when another collection owns it. */
function targetCollection(element, collectionId, cardsById) {
	const target = cardsById?.[normalizeId(element.lookupCardId)];
	const owner = target?.scope === 'collection' ? normalizeId(target.collection_id) : '';
	return owner || collectionId;
}

/** { [element name]: { [lowercased name]: { id } | { ambiguous } } } for the name columns mapped. */
async function settleNames({ sheet, mapping, elements, collectionId, cardsById, getElementsForCard }) {
	const names = {};
	for (const { column, element } of mappedColumns(sheet.columns, mapping, elements)) {
		if (isNameType(element.dataType)) {
			const asked = namesInColumn(element, sheet.rows.map(row => row.cells[column.index]));
			// eslint-disable-next-line no-await-in-loop
			names[element.name] = element.dataType === 'user'
				? await resolvePeople({ collectionId, names: asked, getCollaborators: getCollectionCollaborators, searchByEmail: searchUsersByEmail })
				: await resolveRecordNames({
					collectionId: targetCollection(element, collectionId, cardsById),
					cardId: normalizeId(element.lookupCardId),
					names: asked,
					cardsById,
					getElementsForCard,
					fetchPage: fetchLookupPage,
				});
		}
	}
	return names;
}

/**
 * The check and the import, run for one importer (KPR-1899).
 *
 *   check   { phase: 'idle' | 'names' | 'rows' | 'done' | 'error', done, total, result, error }
 *           result: { ready, failed, empty, planRefusal, signing }
 *   commit  { phase: 'idle' | 'running' | 'done', done, total, result }
 *           result: { landed, failed, unsent, planRefusal, stopped, error }
 *
 * `runCheck(input)` reads the names and dry-runs the rows; every Check mints a
 * new run, so nothing about a mapping the person went back on carries over.
 * `prepareCommit()` asks for a signature a signing card wants, before anything
 * moves. `runCommit()` sends what the check found ready; called again after a stop or
 * a lost connection it sends only what is left, under the same import, adding
 * to what landed. `requestSignature(count)` resolves a token, or null.
 *
 * A run that outlives the component stops at its next batch.
 */
export default function useImportRun({ collectionId, card, send, zone, cardsById, getElementsForCard, requestSignature }) {
	const [check, setCheck] = useState(IDLE);
	const [commit, setCommit] = useState(IDLE);
	const alive = useRef(true);
	const stopRef = useRef(false);
	const runRef = useRef(null);
	const signatureRef = useRef(null);
	const generation = useRef(0);

	useEffect(() => {
		alive.current = true;
		return () => { alive.current = false; };
	}, []);

	const cardsRef = useRef({ cardsById, getElementsForCard });
	cardsRef.current = { cardsById, getElementsForCard };

	const resetRun = useCallback(() => {
		generation.current += 1;
		runRef.current = null;
		signatureRef.current = null;
		setCheck(IDLE);
		setCommit(IDLE);
	}, []);

	const runCheck = useCallback(async ({ sheet, mapping, elements, fileName, dateDefault }) => {
		generation.current += 1;
		const mine = generation.current;
		const current = () => alive.current && mine === generation.current;
		setCommit(IDLE);
		setCheck({ ...IDLE, phase: 'names' });
		try {
			const names = await settleNames({ sheet, mapping, elements, collectionId, ...cardsRef.current });
			if (!current()) return;
			const { rows, empty } = buildRows({ sheet, mapping, elements, names, zone, dateDefault });
			const run = mintRun(fileName);
			setCheck({ ...IDLE, phase: 'rows', total: rows.length });
			const result = await checkRows({
				rows,
				card: card.id,
				run,
				send,
				isStopped: () => !current(),
				onProgress: done => { if (current()) setCheck(prev => ({ ...prev, done })); },
			});
			if (!current() || !result) return;
			runRef.current = run;
			setCheck({ ...IDLE, phase: 'done', done: rows.length, total: rows.length, result: { ...result, empty } });
		} catch (error) {
			if (current()) setCheck({ ...IDLE, phase: 'error', error });
		}
	}, [card, collectionId, send, zone]);

	const stopCommit = useCallback(() => {
		stopRef.current = true;
		setCommit(prev => ({ ...prev, stopping: true }));
	}, []);

	/** Whatever a commit needs from the person first. False when they declined. */
	const prepareCommit = useCallback(async () => {
		if (!check.result?.signing || signatureRef.current) return true;
		signatureRef.current = await requestSignature(check.result.ready.length);
		return Boolean(signatureRef.current);
	}, [check.result, requestSignature]);

	const runCommit = useCallback(async () => {
		const run = runRef.current;
		const earlier = commit.result;
		const rows = earlier ? earlier.unsent : check.result?.ready;
		if (!run || !rows?.length) return;
		stopRef.current = false;
		const landedBefore = earlier?.landed || 0;
		const failedBefore = earlier?.failed || [];
		const total = landedBefore + failedBefore.length + rows.length;
		setCommit({ ...IDLE, phase: 'running', done: landedBefore + failedBefore.length, total, result: earlier });
		const result = await commitRows({
			rows,
			card: card.id,
			run,
			send,
			signature: signatureRef.current,
			sign: requestSignature,
			isStopped: () => stopRef.current || !alive.current,
			onProgress: done => { if (alive.current) setCommit(prev => ({ ...prev, done: landedBefore + failedBefore.length + done })); },
		});
		signatureRef.current = result.signature;
		if (!alive.current) return;
		setCommit({
			...IDLE,
			phase: 'done',
			done: total,
			total,
			result: { ...result, landed: landedBefore + result.landed, failed: [...failedBefore, ...result.failed], planRefusal: result.planRefusal || earlier?.planRefusal || null },
		});
	}, [card, check.result, commit.result, requestSignature, send]);

	return useMemo(() => ({ check, commit, runCheck, prepareCommit, runCommit, stopCommit, resetRun }), [check, commit, runCheck, prepareCommit, runCommit, stopCommit, resetRun]);
}
